import { useCallback, useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import { useT } from "../contexts/TranslationContext";
import Button from "./ui/Button";

/**
 * Admin controls for the external project client share link.
 */
export default function ProjectClientSharePanel({ projectId, enabled = false }) {
  const { t } = useT();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [label, setLabel] = useState("");
  const [copied, setCopied] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);

  const load = useCallback(async () => {
    if (!projectId || !enabled) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/projects/${projectId}/client-share`);
      setShare(res.data);
      setLabel(res.data?.label || "");
      if (res.data?.token) setRevealedToken(res.data.token);
    } catch (err) {
      console.error(err);
      setError(t("projectClientShare.loadError"));
    } finally {
      setLoading(false);
    }
  }, [projectId, enabled, t]);

  useEffect(() => {
    load();
  }, [load]);

  const absoluteShareUrl = (pathOrToken) => {
    const path =
      pathOrToken?.startsWith("/share/")
        ? pathOrToken
        : pathOrToken
          ? `/share/p/${pathOrToken}`
          : share?.sharePath;
    if (!path) return "";
    return `${window.location.origin}${path}`;
  };

  const save = async (payload) => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.put(`/projects/${projectId}/client-share`, payload);
      setShare(res.data);
      if (res.data?.token) setRevealedToken(res.data.token);
      setPin("");
      return res.data;
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || t("projectClientShare.saveError"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    await save({
      enabled: true,
      label: label.trim(),
      ...(pin.trim() ? { pin: pin.trim() } : {}),
    });
  };

  const handleDisable = async () => {
    await save({ enabled: false });
  };

  const handleSaveSettings = async () => {
    const payload = { label: label.trim(), enabled: true };
    if (pin.trim()) payload.pin = pin.trim();
    await save(payload);
  };

  const handleClearPin = async () => {
    await save({ clearPin: true, enabled: true });
  };

  const handleRegenerate = async () => {
    if (!window.confirm(t("projectClientShare.regenerateConfirm"))) return;
    await save({ regenerate: true, enabled: true });
  };

  const handleRemove = async () => {
    if (!window.confirm(t("projectClientShare.removeConfirm"))) return;
    setSaving(true);
    setError("");
    try {
      await axios.delete(`/projects/${projectId}/client-share`);
      setShare({ exists: false, enabled: false, hasPin: false, label: "", sharePath: null });
      setRevealedToken(null);
      setLabel("");
      setPin("");
    } catch (err) {
      console.error(err);
      setError(t("projectClientShare.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    const url = absoluteShareUrl(revealedToken || share?.sharePath);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t("projectClientShare.copyManual"), url);
    }
  };

  if (!enabled) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">{t("projectClientShare.title")}</h2>
        <p className="text-sm text-gray-600 mt-1">{t("projectClientShare.description")}</p>
      </div>

      {loading && (
        <p className="text-sm text-gray-500">{t("projectDetails.loading")}</p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && (
        <>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {t("projectClientShare.label")}
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("projectClientShare.labelPlaceholder")}
                maxLength={120}
              />
            </label>

            <label className="block text-sm font-medium text-gray-700">
              {t("projectClientShare.pin")}
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder={
                  share?.hasPin
                    ? t("projectClientShare.pinChangePlaceholder")
                    : t("projectClientShare.pinPlaceholder")
                }
              />
            </label>
            <p className="text-xs text-gray-500">{t("projectClientShare.pinHint")}</p>
            {share?.hasPin && (
              <p className="text-xs text-gray-600">{t("projectClientShare.pinActive")}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!share?.exists || !share?.enabled ? (
              <Button type="button" onClick={handleEnable} disabled={saving}>
                {t("projectClientShare.enable")}
              </Button>
            ) : (
              <>
                <Button type="button" onClick={handleSaveSettings} disabled={saving}>
                  {t("projectClientShare.save")}
                </Button>
                {share.hasPin && (
                  <Button type="button" variant="outline" onClick={handleClearPin} disabled={saving}>
                    {t("projectClientShare.clearPin")}
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={handleDisable} disabled={saving}>
                  {t("projectClientShare.disable")}
                </Button>
                <Button type="button" variant="outline" onClick={handleRegenerate} disabled={saving}>
                  {t("projectClientShare.regenerate")}
                </Button>
                <Button type="button" variant="outline" onClick={handleRemove} disabled={saving}>
                  {t("projectClientShare.remove")}
                </Button>
              </>
            )}
          </div>

          {share?.exists && share?.enabled && share?.sharePath && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                {t("projectClientShare.link")}
              </p>
              <p className="text-sm break-all text-gray-900 font-mono">
                {absoluteShareUrl(revealedToken || share.sharePath)}
              </p>
              <Button type="button" variant="outline" onClick={copyLink}>
                {copied ? t("projectClientShare.copied") : t("projectClientShare.copyLink")}
              </Button>
              {share.lastAccessedAt && (
                <p className="text-xs text-gray-500">
                  {t("projectClientShare.lastAccessed", {
                    date: new Date(share.lastAccessedAt).toLocaleString(),
                  })}
                </p>
              )}
            </div>
          )}

          {share?.exists && !share?.enabled && (
            <p className="text-sm text-amber-700">{t("projectClientShare.disabledNotice")}</p>
          )}
        </>
      )}
    </div>
  );
}
